/**
 * CoherenceBanner — V14.62 Sprint 23 (Single-House).
 *
 * Affiche en temps réel l'état de cohérence verticale d'une maison :
 *   • Hauteur cumulée des étages
 *   • Nombre de liaisons verticales (escaliers chainés correctement)
 *   • Escaliers orphelins (sans pendant à l'étage opposé)
 *   • Warnings (calibration manquante, échelles incohérentes…)
 *
 * Le composant ré-interroge le backend `/coherence` à chaque save d'étage,
 * via la prop `refreshKey` que le parent incrémente.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, AlertTriangle, Layers3, Link2, Link2Off, RotateCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoherenceBanner = ({ houseId, refreshKey }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!houseId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/single-house/projects/${houseId}/coherence`,
          { withCredentials: true },
        );
        if (!cancelled) setReport(data);
      } catch {
        if (!cancelled) setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [houseId, refreshKey]);

  if (!report) return null;

  const hasIssues = report.issues.length > 0;
  const linkCount = report.vertical_links.length;
  const orphanCount = report.orphan_stairs.length;

  return (
    <div
      className="rounded-lg border p-3 space-y-2"
      style={{
        background: hasIssues ? 'rgba(245,158,11,0.06)' : 'rgba(16,185,129,0.06)',
        borderColor: hasIssues ? 'rgba(245,158,11,0.30)' : 'rgba(16,185,129,0.30)',
      }}
      data-testid="coherence-banner"
    >
      <div className="flex items-center gap-2">
        {hasIssues
          ? <AlertTriangle className="w-4 h-4 text-amber-400" />
          : <ShieldCheck className="w-4 h-4 text-emerald-400" />}
        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold"
             style={{ color: hasIssues ? '#fcd34d' : '#86efac' }}>
          Cohérence verticale
        </div>
        <div className="flex-1" />
        {loading && <RotateCw className="w-3 h-3 text-gray-400 animate-spin" />}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <span className="inline-flex items-center gap-1 text-gray-300">
          <Layers3 className="w-3 h-3 text-emerald-400" />
          {report.floors_count} étages · hauteur cumulée{' '}
          <span className="font-mono text-emerald-200">{report.cumulative_height_m.toFixed(2)} m</span>
        </span>
        <span className="inline-flex items-center gap-1 text-gray-300"
              data-testid="coherence-links-count">
          <Link2 className="w-3 h-3 text-emerald-400" />
          {linkCount} liaison{linkCount > 1 ? 's' : ''} verticale{linkCount > 1 ? 's' : ''}
        </span>
        <span className={`inline-flex items-center gap-1 ${orphanCount > 0 ? 'text-amber-300' : 'text-gray-500'}`}
              data-testid="coherence-orphans-count">
          <Link2Off className="w-3 h-3" />
          {orphanCount} orphelin{orphanCount > 1 ? 's' : ''}
        </span>
      </div>

      {hasIssues && (
        <ul className="space-y-1 pt-1 border-t border-amber-400/15"
            data-testid="coherence-issues-list">
          {report.issues.map((issue, idx) => (
            <li key={`${issue.code}-${idx}`}
                className="text-[10.5px] text-amber-200/95 leading-snug pl-4 relative">
              <span className="absolute left-0 top-1.5 w-1 h-1 rounded-full bg-amber-400" />
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CoherenceBanner;
